export const ReasoningEffortEnumValues = ["low", "medium", "high"] as const;
export type ReasoningEffortEnumType =
  (typeof ReasoningEffortEnumValues)[number];

// Chat type enum
export const ChatTypeEnumValues = ["COMPLETION", "REALTIME"] as const;
export type ChatTypeEnumType = (typeof ChatTypeEnumValues)[number];

// messageStatusEnum
export const MessageStatusEnumValues = [
  "PENDING",
  "SUCCESS",
  "ERROR",
  "CANCELLED",
] as const;
export type MessageStatusEnumType = (typeof MessageStatusEnumValues)[number];

// Realtime conversation item role enum
export const RealtimeConversationItemRoleEnumValues = [
  "user",
  "assistant",
  "system",
] as const;

// messageRoleEnum. Lower-case, because we match the OpenAI message types.
export const MessageRoleEnumValues = [
  "system",
  "user",
  "assistant",
  "tool",
] as const;
export type MessageRoleEnumType = (typeof MessageRoleEnumValues)[number];

// Origins of files produced by tools in a chat, as opposed to user uploads.
export const GeneratedMessageFileOriginEnumValues = [
  "PLUGIN_OUTPUT",
  "CODE_RESULT",
] as const;

// Any way a file gets attached to a message: uploaded by the user, or generated.
export const MessageFileOriginEnumValues = [
  "USER_INPUT",
  ...GeneratedMessageFileOriginEnumValues,
] as const;

export type MessageFileOriginEnumType =
  (typeof MessageFileOriginEnumValues)[number];

// feedbackValueEnum
export const FeedbackValueEnumValues = ["LIKE", "DISLIKE"] as const;
export type FeedbackValueEnumType = (typeof FeedbackValueEnumValues)[number];

/**
 * tokenUsageSourceEnum
 *
 * Which Chat AI code path consumed the tokens. Used to tell the user-facing
 * completion apart from the utility model calls we make on the user's behalf.
 *
 * - `CHAT`: the chat completion the user is waiting for.
 * - `TITLE`: automatic chat title generation.
 * - `COMPRESSION`: model calls that checkpoint conversation context.
 * - `TOOL`: model calls made from inside a plugin tool.
 * - `CITATION`: extracting the cited passages behind a citation.
 * - `ISEARCH`: filling an iSearch AI column cell. Runs a profile like a chat
 *   turn does, but per spreadsheet row and at high fan-out, so it is worth
 *   telling apart from what a person typed.
 * - `SEED`: model calls made by the Chat AI seeds page on development
 *   environments, interpreting a prompt and producing seeded files. Kept
 *   apart so seeding never reads as real usage.
 */
export const TokenUsageSourceEnumValues = [
  "CHAT",
  "TITLE",
  "COMPRESSION",
  "TOOL",
  "CITATION",
  "ISEARCH",
  "SEED",
] as const;
export type TokenUsageSourceEnumType =
  (typeof TokenUsageSourceEnumValues)[number];

// workspaceUserRoleEnum
export const RoleEnumValues = ["ADMIN", "USER"] as const;
export type RoleEnumTypes = (typeof RoleEnumValues)[number];

// workspaceTypeEnum
// Workspace types - split into public and private. This is to allow validator to prevent use of PERSONAL
export const WorkspaceTypeValuesPrivate = ["PERSONAL"] as const;
export const WorkspaceTypeValuesPublic = ["CONTEXTED", "BASIC_SHARED"] as const;
export const WorkspaceTypeValuesAll = [
  ...WorkspaceTypeValuesPublic,
  ...WorkspaceTypeValuesPrivate,
] as const;
export type WorkspaceTypeEnumType = (typeof WorkspaceTypeValuesAll)[number];

// Visible plugins
export const VisiblePluginTypeEnumValues = [
  "CLINICAL_TRIALS_GOV",
  "PUBMED",
  "WEBREADER",
  "SERVICE_NOW_KNOWLEDGE_ARTICLES",
  "RESEARCH_AI",
  "MEDDRA",
  "ISEARCH",
  "HAS_DATABASE",
] as const;

// Hidden plugins
export const HiddenPluginTypeEnumValues = [
  "MS_OFFICE_WRITER",
  "CODE_INTERPRETER",
  "IMAGE_GENERATION",
  "TEXT_FILE_GENERATOR",
  "ONEDRIVE",
  "CALENDAR",
  "PROFILE_CREATION",
] as const;
export type HiddenPluginTypeEnumTypes =
  (typeof HiddenPluginTypeEnumValues)[number];

// All plugins
export const PluginTypeEnumValues = [
  ...VisiblePluginTypeEnumValues,
  ...HiddenPluginTypeEnumValues,
] as const;
export type PluginTypeEnumTypes = (typeof PluginTypeEnumValues)[number];

// Utility function to check if a plugin type is hidden
export const isHiddenPluginType = (
  pluginType: PluginTypeEnumTypes,
): pluginType is HiddenPluginTypeEnumTypes =>
  HiddenPluginTypeEnumValues.includes(pluginType as HiddenPluginTypeEnumTypes);

// workspaceTypeEnum
export const WorkspaceTypeValues = ["CHAT", "CHAT_HISTORY"] as const;
export type WorkspaceTypeEnumTypes = (typeof WorkspaceTypeValues)[number];

// Chat workspace types
export const ChatWorkspaceTypeValues = ["CHAT", "CHAT_HISTORY"] as const;
export type ChatWorkspaceTypeEnumTypes =
  (typeof ChatWorkspaceTypeValues)[number];

/**
 * AiProfilePopulatedStatusEnum
 *
 * This enum represents the various statuses of an AI profile.
 *
 * - `DRAFT`: The AI profile is in draft status and not yet published. Will be displayed for admins only in profiles list in ChatAI app.
 * - `PUBLISHED`: The AI profile is published and available for use. Will be displayed for all users.
 * - `RETIRED`: The AI profile is retired and no longer in active use. Will be displayed only for admins in the ChatAI section in admin panel.
 */
export const AiProfilePopulatedStatusEnumValues = [
  "DRAFT",
  "PUBLISHED",
  "RETIRED",
] as const;
export type AiProfilePopulatedStatusEnumType =
  (typeof AiProfilePopulatedStatusEnumValues)[number];

/** "pending" = computation in progress; "completed" = highlights are ready to display. */
export const CitationHighlightCacheStatusEnumValues = [
  "pending",
  "completed",
] as const;
export type CitationHighlightCacheStatusEnumType =
  (typeof CitationHighlightCacheStatusEnumValues)[number];

export const AiProfileShareTypeEnumValues = [
  "USERS",
  "WORKSPACE",
  "GALLERY",
] as const;
